import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../store/reducers/snackbarSlice';
import { Company } from '../../../types';
import {
  CompanyThreeWayCall,
  CompanyThreeWayCallsPage,
  ThreeWayCallerFilter,
  CompanyWidget,
  createCompanyWidget,
  getCompanyThreeWayCalls,
  getCompanyWidget,
  sendCompanyWidgetEmail,
} from '../companyHelpers';
import WidgetDutyBoard from '../WidgetDutyBoard/WidgetDutyBoard';
import style from './WidgetQrModal.module.css';

interface WidgetQrModalProps {
  /** §FB37: вкладка сторінки компанії — без оверлею та кнопки закриття. */
  embedded?: boolean;
  companyId: number;
  company?: Company;
  onClose: () => void;
}

const QrGlyph = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3M21 14v7h-7M17 21v-4" />
  </svg>
);
const PersonGlyph = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const CopyGlyph = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const DownloadGlyph = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const MailGlyph = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-10 5L2 7" />
  </svg>
);

// Дата/час надсилання у київському поясі: «08.07.2026, 19:14».
const formatSentAt = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString('uk-UA', {
      timeZone: 'Europe/Kyiv',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d.toLocaleString('uk-UA');
  }
};

// §FB42: тривалість розмови «2 хв 05 с».
const formatCallDuration = (secs?: number | null): string => {
  if (!secs || secs <= 0) return '—';
  const m = Math.floor(secs / 60);
  const sRest = secs % 60;
  return m > 0 ? `${m} хв ${String(sRest).padStart(2, '0')} с` : `${sRest} с`;
};

// Бейдж результату виклику (outcome рахує бекенд).
const OUTCOME_VIEW: Record<string, { label: string; tone: 'ok' | 'live' | 'bad' }> = {
  finished: { label: 'Відбувся', tone: 'ok' },
  active: { label: 'Триває', tone: 'live' },
  ringing: { label: 'Дзвонить…', tone: 'live' },
  // §FB170: canceled — дзвінок завершився до з'єднання: поклав слухавку абонент або відхилив
  // перекладач. «Відхилено» вводило в оману, коли скасовував сам абонент.
  canceled: { label: 'Скасовано', tone: 'bad' },
  missed: { label: 'Без відповіді', tone: 'bad' },
};

// §FB170: колонка «Відповідальний» — безособові форми (не залежать від роду).
const responsibleText = (c: CompanyThreeWayCall): string => {
  switch (c.responsibleOutcome) {
    case 'answered':
      return c.channel === 'web'
        ? 'прийнято в кабінеті'
        : 'прийнято на телефоні';
    case 'declined':
      return 'відхилено';
    case 'missed':
      return 'пропущено';
    default:
      return '—';
  }
};

const WidgetQrModal: React.FC<WidgetQrModalProps> = ({
  companyId,
  company,
  onClose,
  embedded = false,
}) => {
  const dispatch = useDispatch();
  const [widget, setWidget] = useState<CompanyWidget | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [view, setView] = useState<'qr' | 'email'>('qr');
  const [creating, setCreating] = useState<boolean>(false);

  const [recipientEmail, setRecipientEmail] = useState(company?.email ?? '');
  const [sending, setSending] = useState<boolean>(false);
  // §FB42: журнал 3-сторонніх викликів (ініціатор + відп. особа + перекладач).
  const [twData, setTwData] = useState<CompanyThreeWayCallsPage | null>(null);
  const [twItems, setTwItems] = useState<CompanyThreeWayCall[]>([]);
  const [twPage, setTwPage] = useState<number>(0);
  const [twLoading, setTwLoading] = useState<boolean>(false);
  // §FB175: фільтр за тим, звідки дзвонив абонент; показуємо його, щойно бекенд віддає канал (новий бекенд).
  const [twFilter, setTwFilter] = useState<ThreeWayCallerFilter>('');
  const [twChannelAware, setTwChannelAware] = useState<boolean>(false);

  const companyName = company?.name;

  const load = () => {
    setLoading(true);
    setError(false);
    return getCompanyWidget(companyId)
      .then((data) => {
        setWidget(data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getCompanyWidget(companyId)
      .then((data) => {
        if (!active) return;
        setWidget(data);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [companyId]);

  const copyUrl = (url: string) => {
    const notify = (ok: boolean) =>
      dispatch(
        showSnackbar({
          message: ok
            ? 'Посилання скопійовано'
            : 'Не вдалося скопіювати. Виділіть поле та натисніть Ctrl+C',
          type: ok ? 'success' : 'error',
        }),
      );

    // Легасі-копіювання (працює і в незахищеному контексті, напр. HTTP-стенд),
    // виконується синхронно в межах кліку, щоб браузер дозволив копіювання.
    const legacyCopy = (): boolean => {
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '0';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, url.length);
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
      } catch {
        return false;
      }
    };

    // Clipboard API доступний лише в захищеному контексті (HTTPS/localhost).
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(url).then(
        () => notify(true),
        () => notify(legacyCopy()),
      );
    } else {
      notify(legacyCopy());
    }
  };

  const handleDownload = () => {
    if (!widget?.qrPngBase64) return;
    const safeName = (widget.companyName || 'company')
      .replace(/[\\/:*?"<>|]+/g, '_')
      .trim()
      .slice(0, 60);
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${widget.qrPngBase64}`;
    link.download = `QR_${safeName}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // §FB42: сторінка журналу 3-сторонніх викликів; «Показати ще» довантажує з дедупом.
  const loadTw = async (
    pageIndex: number,
    replace: boolean,
    filter: ThreeWayCallerFilter = twFilter,
  ) => {
    setTwLoading(true);
    try {
      const data = await getCompanyThreeWayCalls(
        companyId,
        pageIndex,
        30,
        filter,
      );
      if (data.items.some((i) => i.callerClient != null)) {
        setTwChannelAware(true);
      }
      setTwData(data);
      setTwItems((prev) =>
        replace
          ? data.items
          : [
              ...prev,
              ...data.items.filter((n) => !prev.some((p) => p.id === n.id)),
            ],
      );
      setTwPage(pageIndex);
    } catch {
      if (replace) setTwItems([]);
    } finally {
      setTwLoading(false);
    }
  };

  useEffect(() => {
    loadTw(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const fresh = await createCompanyWidget(companyId);
      setWidget(fresh);
      dispatch(
        showSnackbar({ message: 'Посилання створено', type: 'success' }),
      );
    } catch (e: any) {
      const msg =
        e?.response?.data?.Message ||
        e?.response?.data?.message ||
        'Не вдалося створити посилання';
      dispatch(showSnackbar({ message: msg, type: 'error' }));
      // Оновлюємо стан (раптом відповідальну особу вже додали/прибрали).
      load();
    } finally {
      setCreating(false);
    }
  };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim());

  const handleSend = async () => {
    if (!emailValid || sending) return;
    setSending(true);
    try {
      await sendCompanyWidgetEmail({
        companyId,
        recipientEmail: recipientEmail.trim(),
      });
      dispatch(showSnackbar({ message: 'Лист надіслано', type: 'success' }));
      // Оновлюємо стан, щоб під кнопками зʼявився новий запис історії надсилань.
      try {
        const fresh = await getCompanyWidget(companyId);
        setWidget(fresh);
      } catch {
        /* ігноруємо — лист усе одно відправлено */
      }
      setView('qr');
    } catch (e) {
      dispatch(
        showSnackbar({ message: 'Не вдалося надіслати листа', type: 'error' }),
      );
    } finally {
      setSending(false);
    }
  };

  const created =
    !!widget?.exists && !!widget.widgetUrl && !!widget.qrPngBase64;

  return (
    <div
      className={embedded ? style.embeddedWrap : style.modalOverlay}
      onClick={embedded ? undefined : onClose}
    >
      <div
        className={`${style.modal} ${view === 'email' ? style.modalNarrow : ''} ${embedded ? style.modalEmbedded : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {!embedded && (
          <div className={style.header}>
            <h5 className={style.title}>
              {view === 'qr' ? 'QR для віджету' : 'Надіслати QR на email'}
            </h5>
            {!embedded && (
              <button
                className={style.closeButton}
                onClick={onClose}
                aria-label="Закрити"
              >
                ×
              </button>
            )}
          </div>
        )}

        <div className={style.body}>
          {companyName && !(view === 'qr' && created) && (
            <p className={style.company}>{companyName}</p>
          )}

          {view === 'qr' && (
            <>
              {loading && <p className={style.muted}>Завантаження…</p>}
              {error && (
                <p className={style.errorText}>
                  Не вдалося завантажити. Спробуйте ще раз.
                </p>
              )}

              {widget && !loading && !error && created && (
                <>
                <div className={style.createdRow}>
                  <div className={style.qrCol}>
                    <div className={style.qrWrap}>
                      <img
                        className={style.qr}
                        src={`data:image/png;base64,${widget.qrPngBase64}`}
                        alt="QR-код віджету"
                      />
                    </div>
                    <p className={style.qrHint}>
                      Скануйте камерою застосунку WordCraft
                    </p>
                  </div>

                  <div className={style.vDivider} />

                  <div className={style.infoCol}>
                    {companyName && (
                      <p className={style.company}>{companyName}</p>
                    )}
                    <p className={style.subhint}>
                      Постійне посилання для виклику перекладача
                    </p>

                    <label className={style.urlLabel}>Постійне посилання</label>
                    <input
                      className={style.urlField}
                      type="text"
                      readOnly
                      value={widget.widgetUrl}
                      onFocus={(e) => e.currentTarget.select()}
                    />

                    <div className={style.actionsRow}>
                      <button
                        className={style.primaryBtn}
                        onClick={() => copyUrl(widget.widgetUrl!)}
                      >
                        <CopyGlyph className={style.btnIcon} />
                        <span>Копіювати посилання</span>
                      </button>
                      <button
                        className={style.secondaryBtn}
                        onClick={handleDownload}
                      >
                        <DownloadGlyph className={style.btnIcon} />
                        <span>Завантажити QR</span>
                      </button>
                      <button
                        className={style.secondaryBtn}
                        onClick={() => setView('email')}
                      >
                        <MailGlyph className={style.btnIcon} />
                        <span>Надіслати на email</span>
                      </button>
                    </div>

                  </div>
                </div>

                {/* §FB170: хто з відповідальних зараз чергує у вебкабінеті викликів — для будь-якої
                    компанії, щойно кабінет працює в системі (перемикача компанії з 27.09 немає). */}
                {company?.widgetCabinetGlobalEnabled && (
                  <WidgetDutyBoard companyId={companyId} />
                )}

                {/* §FB42: журнал 3-сторонніх викликів — під блоком посилання/коду
                    (історія надсилань листів — на вкладці «Історія листів»). */}
                <div className={style.twSection}>
                  <div className={style.twHead}>
                    <p className={style.twTitle}>3-сторонні виклики</p>
                    {twData && twData.totalCount > 0 && (
                      <span className={style.twCount}>{twData.totalCount}</span>
                    )}
                    {(twChannelAware || twFilter !== '') && (
                      <select
                        className={style.twFilter}
                        value={twFilter}
                        aria-label="Звідки дзвонив абонент"
                        title="Звідки дзвонив абонент"
                        onChange={(e) => {
                          const next = e.target.value as ThreeWayCallerFilter;
                          setTwFilter(next);
                          loadTw(0, true, next);
                        }}
                        disabled={twLoading}
                      >
                        <option value="">Усі канали</option>
                        <option value="app">Із застосунку</option>
                        <option value="web">З браузера</option>
                      </select>
                    )}
                    <button
                      type="button"
                      className={style.twRefresh}
                      onClick={() => loadTw(0, true)}
                      disabled={twLoading}
                      title="Оновити список викликів"
                      aria-label="Оновити список викликів"
                    >
                      ⟳
                    </button>
                  </div>
                  {twLoading && twItems.length === 0 && (
                    <p className={style.twMuted}>Завантаження…</p>
                  )}
                  {!twLoading && twItems.length === 0 && (
                    <p className={style.twMuted}>
                      {twFilter === ''
                        ? '3-сторонніх викликів через QR-віджет ще не було.'
                        : twFilter === 'web'
                          ? 'Викликів із браузера ще не було.'
                          : 'Викликів із застосунку ще не було.'}
                    </p>
                  )}
                  {twItems.length > 0 && (
                    <div className={style.twList}>
                      <div className={`${style.twRow} ${style.twHeadRow}`}>
                        <span>Дата і час</span>
                        <span>Учасники</span>
                        <span>Відповідальний</span>
                        <span>Тривалість</span>
                        <span>Статус</span>
                      </div>
                      {twItems.map((c) => {
                        const view3 =
                          OUTCOME_VIEW[c.outcome] ?? OUTCOME_VIEW.missed;
                        const toneClass =
                          view3.tone === 'ok'
                            ? style.twOk
                            : view3.tone === 'live'
                              ? style.twLive
                              : style.twBad;
                        const parts = [c.receiverName, c.thirdName]
                          .filter(Boolean)
                          .join(', ');
                        return (
                          <div key={c.id} className={style.twRow}>
                            <span className={style.twDate}>
                              {formatSentAt(c.createdAt)}
                            </span>
                            <span className={style.twParts}>
                              <b>{c.requesterName || '—'}</b>
                              {(c.callerClient === 'web-desktop' ||
                                c.callerClient === 'web-mobile') && (
                                <span
                                  className={style.twVia}
                                  title={
                                    c.callerClient === 'web-mobile'
                                      ? 'Дзвонив із браузера телефона'
                                      : "Дзвонив із браузера комп'ютера"
                                  }
                                >
                                  {' · з браузера'}
                                </span>
                              )}
                              {' → '}
                              {parts || '—'}
                            </span>
                            <span
                              className={style.twResp}
                              title={
                                c.channel === 'web'
                                  ? 'Дзвонило в кабінеті викликів'
                                  : c.channel === 'phone'
                                    ? 'Дзвонило на телефон'
                                    : undefined
                              }
                            >
                              {responsibleText(c)}
                            </span>
                            <span className={style.twDur}>
                              {formatCallDuration(c.durationSeconds)}
                            </span>
                            <span className={`${style.twBadge} ${toneClass}`}>
                              {view3.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {twData?.hasNextPage && twItems.length > 0 && (
                    <button
                      type="button"
                      className={style.twMore}
                      onClick={() => loadTw(twPage + 1, false)}
                      disabled={twLoading}
                    >
                      {twLoading ? 'Завантаження…' : 'Показати ще'}
                    </button>
                  )}
                </div>
                </>
              )}

              {widget && !loading && !error && !created && (
                <div className={style.emptyState}>
                  <span className={style.emptyIcon}>
                    <QrGlyph />
                  </span>
                  {widget.hasResponsiblePerson ? (
                    <>
                      <p className={style.emptyTitle}>
                        Посилання ще не створено
                      </p>
                      <p className={style.emptyNote}>
                        Створіть постійне посилання та QR-код для виклику
                        перекладача. Після створення воно не змінюється.
                      </p>
                      <button
                        className={style.createBtn}
                        onClick={handleCreate}
                        disabled={creating}
                      >
                        <QrGlyph />
                        <span>
                          {creating ? 'Створення…' : 'Створити посилання та QR'}
                        </span>
                      </button>
                    </>
                  ) : (
                    <>
                      <p className={style.emptyTitle}>
                        Немає відповідальної особи
                      </p>
                      <div className={style.warnNote}>
                        <PersonGlyph />
                        <span>
                          Щоб створити посилання, спочатку призначте
                          відповідальну особу в розділі «Співробітники» цієї
                          компанії.
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {view === 'email' && (
            <div className={style.form}>
              <label className={style.fieldLabel}>Email отримувача</label>
              <input
                className={style.field}
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="contractor@example.com"
                autoFocus
              />

              <div className={style.actions}>
                <button
                  className={style.secondaryBtn}
                  onClick={() => setView('qr')}
                  disabled={sending}
                >
                  Назад
                </button>
                <button
                  className={style.primaryBtn}
                  onClick={handleSend}
                  disabled={!emailValid || sending}
                >
                  {sending ? 'Надсилання…' : 'Надіслати'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetQrModal;
